import React, { useEffect } from "react";
import {
  MdMic,
  MdVideocam,
  MdScreenShare,
  MdVideocamOff,
  MdStopScreenShare,
  MdMicOff,
  MdStop,
  MdFlipCameraIos,
} from "react-icons/md";
import { PiVideoCameraSlashDuotone } from "react-icons/pi";
import { useBroadcastManagerContext } from "./components/BroadcastManagerContext";
import LoadingOverlay from "./components/LoadingOverlay";
import { ILiveStreamData } from "../../lib/interfaces";
import { isMobileScreen } from "../../lib/utils";

const LiveStream: React.FC<ILiveStreamData> = ({ videoRef }) => {
  const {
    micOn,
    cameraEnabled,
    screenSharing,
    hasVideoPermission,
    hasAudioPermission,
    getCameras,
    toggleMicrophone,
    toggleCamera,
    handleCameraFlip,
    toggleScreenShare,
    stopBroadcast,
    cameras,
    broadcaster,
    loadingProgress,
    loadingMessage,
    onLeaveClean,
  } = useBroadcastManagerContext();

  const isMobile = /iPhone|iPad|iPod|android/i.test(navigator.userAgent);
  const isWindowLarge = window.innerWidth > 1680;

  useEffect(() => {
    if (isMobile) {
      getCameras();
    }
  }, [isMobile, getCameras]);

  useEffect(() => {
    // Flag to mark that the user confirmed leaving
    let isConfirmedUnload = false;

    const handleVisibilityChange = async () => {
      if (
        document.visibilityState === "hidden" &&
        isConfirmedUnload &&
        broadcaster
      ) {
        try {
          await onLeaveClean();
        } catch (error) {
          console.error("Error stopping broadcast:", error);
        }
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (broadcaster) {
        // Trigger the confirmation dialog.
        e.preventDefault();
        e.returnValue =
          "Your live stream will end if you leave or reload the page. Are you sure?";

        // Set the flag so that if the page goes hidden (i.e. user confirms),
        // handleVisibilityChange will trigger the stopBroadcast.
        isConfirmedUnload = true;

        // (Optional) If you use sessionStorage to differentiate a reload,
        // you can set it here:
        sessionStorage.setItem("isReloading", "true");

        // Optionally, reset the flag shortly after in case the user cancels.
        setTimeout(() => {
          if (document.visibilityState !== "hidden") {
            isConfirmedUnload = false;
            sessionStorage.removeItem("isReloading");
          }
        }, 100);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      sessionStorage.removeItem("isReloading");
    };
  }, [broadcaster]);

  return (
    <div className="flex flex-col items-center h-full">
      <div
        className={`relative flex flex-col items-center justify-center w-full ${
          isMobileScreen
            ? "h-[calc(100vh-120px)]"
            : isWindowLarge
            ? "h-[calc(100vh-120px)] pt-10"
            : "h-[calc(100vh-100px)]"
        } `}
      >
        <div className="relative w-full h-full">
          <canvas
            id="preview"
            ref={videoRef}
            className="w-full h-full object-cover rounded-t-lg overflow-hidden "
          />
          {loadingProgress > 0 && loadingProgress < 100 && (
            <LoadingOverlay
              progress={loadingProgress}
              message={loadingMessage || "Starting Stream..."}
            />
          )}
          {(!hasVideoPermission || !cameraEnabled) && !screenSharing && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black">
              <PiVideoCameraSlashDuotone className="text-gray-300 w-20 h-20 mb-4" />
              <p className="text-gray-300 text-lg font-medium">
                {!hasVideoPermission
                  ? "Camera permission needed"
                  : "Camera is turned off"}
              </p>
              <p className="text-gray-400 text-sm">
                {!hasVideoPermission
                  ? "Click the camera button to enable"
                  : "Turn on your camera to share video"}
              </p>
            </div>
          )}
        </div>
        <div className="relative w-full bg-black p-4 rounded-b-lg mt-auto">
          <div className="flex items-center space-x-3 justify-center flex-wrap gap-2">
            <button
              onClick={toggleMicrophone}
              className={`p-3 rounded-full ${
                hasAudioPermission && micOn ? "bg-gray-700" : "bg-red-600"
              } transition-all`}
            >
              {hasAudioPermission && micOn ? (
                <MdMic size={24} className="text-white" />
              ) : (
                <MdMicOff size={24} className="text-white" />
              )}
            </button>
            <button
              onClick={toggleCamera}
              className={`p-3 rounded-full ${
                hasVideoPermission && cameraEnabled
                  ? "bg-gray-700"
                  : "bg-red-600"
              } transition-all`}
            >
              {hasVideoPermission && cameraEnabled ? (
                <MdVideocam size={24} className="text-white" />
              ) : (
                <MdVideocamOff size={24} className="text-white" />
              )}
            </button>
            {isMobile && cameras.length > 1 && hasVideoPermission && (
              <button
                onClick={handleCameraFlip}
                className="p-3 rounded-full bg-gray-700 transition-all"
                disabled={!cameraEnabled}
              >
                <MdFlipCameraIos size={24} className="text-white" />
              </button>
            )}
            {!isMobile && (
              <button
                onClick={toggleScreenShare}
                className={`p-3 rounded-full ${
                  screenSharing ? "bg-green-600" : "bg-gray-700"
                } transition-all`}
              >
                {screenSharing ? (
                  <MdStopScreenShare size={24} className="text-white" />
                ) : (
                  <MdScreenShare size={24} className="text-white" />
                )}
              </button>
            )}
            {isMobile ? (
              <button
                onClick={stopBroadcast}
                className="p-3 rounded-full transition-all bg-gray-700"
              >
                <MdStop size={26} className="text-red-600" />
              </button>
            ) : (
              <button
                onClick={stopBroadcast}
                className="px-5 py-3 rounded-full font-semibold transition-all bg-red-600 text-white text-xs"
              >
                Stop Streaming
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveStream;
